import { useEffect, useRef, useState } from 'react';

export default function StorySection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const expandRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleExpand = () => {
    setIsExpanded(true);
    setTimeout(() => {
      expandRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <section ref={sectionRef} className="py-32 px-6 bg-gradient-to-b from-white to-stone-50">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-stone-800 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Story
          </h2>
          <p className="text-2xl text-stone-700 font-medium" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
            재봉틀 한 대에서 시작된 이야기
          </p>
        </div>

        {/* 핵심 스토리 — 항상 표시 */}
        <div className={`max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="space-y-6">
            <p className="text-lg text-stone-600 leading-relaxed">
              어떤 산업은 거대한 공장에서 시작됩니다.<br />
              하지만 우리의 이야기는 작은 재봉틀 한 대에서 시작되었습니다.
            </p>

            <p className="text-2xl font-semibold text-stone-800" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
              15살 때였습니다.
            </p>

            <p className="text-lg text-stone-600 leading-relaxed">
              플러스 사이즈였던 나는 마음에 드는 옷을 찾기 어려웠습니다.<br />
              그래서 직접 만들어 보기로 했습니다.
            </p>

            <div className="bg-emerald-50 border-l-4 border-emerald-600 p-8 my-8">
              <p className="text-lg text-stone-700 italic leading-relaxed">
                "15살이 만들었는데 정말 잘 만들었다."
              </p>
            </div>

            <p className="text-lg text-emerald-700 font-medium leading-relaxed">
              기술은 사람을 작게 만드는 것이 아니라<br />
              사람을 당당하게 세운다는 것을.
            </p>
          </div>

          {/* 더 보기 버튼 */}
          {!isExpanded && (
            <div className="relative mt-12">
              {/* 페이드 아웃 그라데이션 */}
              <div className="absolute -top-16 left-0 right-0 h-16 bg-gradient-to-b from-transparent to-stone-50 pointer-events-none" />
              <div className="flex flex-col items-center gap-4 pt-4">
                <button
                  onClick={handleExpand}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer group"
                >
                  <span className="text-base font-medium">이야기 더 보기</span>
                  <i className="ri-arrow-down-line text-lg group-hover:translate-y-1 transition-transform duration-300"></i>
                </button>
                <p className="text-sm text-stone-400">기술, 공동체, 지속가능한 미래에 대한 이야기</p>
              </div>
            </div>
          )}
        </div>

        {/* 펼쳐지는 나머지 내용 */}
        {isExpanded && (
          <div
            ref={expandRef}
            className="mt-20 animate-[fadeInUp_0.7s_ease_forwards]"
            style={{ animation: 'fadeInUp 0.7s ease forwards' }}
          >
            <style>{`
              @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(30px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>

            {/* 기술은 사람을 자립하게 합니다 */}
            <div className="grid md:grid-cols-2 gap-16 items-center mb-24 max-w-7xl mx-auto">
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://public.readdy.ai/ai/img_res/0572e9f9cc26d7fc40c35484b63b8617.jpg"
                    alt="기술 교육"
                    className="w-full h-[500px] object-cover object-top"
                  />
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="text-4xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  기술은 사람을 자립하게 합니다
                </h3>
                <p className="text-lg text-stone-600 leading-relaxed">
                  우리는 봉제 기술을 가진 사람들을<br />
                  단순한 노동자가 아니라 <strong className="text-emerald-700">"선생님"</strong>이라고 부릅니다.
                </p>
                <p className="text-lg text-stone-600 leading-relaxed">
                  아이를 키우며 일해야 하는 사람,<br />
                  가정을 돌보면서 경제활동을 해야 하는 사람,<br />
                  다시 일을 시작해야 하는 사람들이 많습니다.
                </p>
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
                  <p className="text-xl font-semibold text-emerald-700 leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                    기술은 사람의 삶을 지키면서도<br />
                    자립할 수 있는 길이 될 수 있다.
                  </p>
                </div>
                <p className="text-lg text-stone-600 leading-relaxed">
                  그렇게 시작된 기술 공동체가<br />
                  지금의 Daygreen입니다.
                </p>
              </div>
            </div>

            {/* 함께 만드는 공동체 */}
            <div className="grid md:grid-cols-2 gap-16 items-center mb-24 max-w-7xl mx-auto">
              <div className="space-y-6">
                <h3 className="text-4xl font-bold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  함께 만드는 공동체
                </h3>
                <p className="text-lg text-stone-600 leading-relaxed">
                  이곳에는 오랜 경험을 가진 봉제 기술자,<br />
                  새롭게 기술을 배우는 사람들,<br />
                  그리고 함께 일하는 공방들이 있습니다.
                </p>
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
                  <p className="text-xl font-semibold text-emerald-700 leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                    작은 공방에서 시작된 작업은<br />
                    이제 지역의 봉제 네트워크와 함께<br />
                    하나의 공동체로 성장하고 있습니다.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=group%20of%20Korean%20artisans%20working%20together%20in%20bright%20sewing%20workshop%20collaborative%20craftsmanship%20multiple%20Korean%20people%20at%20sewing%20machines%20natural%20light%20warm%20atmosphere%20community%20textile%20production%20sustainable%20fashion%20authentic%20teamwork%20professional%20photography%20simple%20background&width=800&height=1000&seq=daygreen-community-work-kr&orientation=portrait"
                    alt="공동체 작업"
                    className="w-full h-[500px] object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* 두 가지 방향 */}
            <div className="max-w-5xl mx-auto mb-24">
              <h3 className="text-4xl font-bold text-stone-800 mb-8 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                기술로 만드는 지속가능한 산업
              </h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-10 rounded-2xl shadow-sm border border-stone-100">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-recycle-line text-3xl text-emerald-700"></i>
                  </div>
                  <h4 className="text-2xl font-semibold text-stone-800 mb-4" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                    지속가능한 패션
                  </h4>
                  <p className="text-base text-stone-600 leading-relaxed mb-4">
                    버려지는 자원을 다시 디자인하고<br />
                    더 오래 사용할 수 있는 제품을 만드는 브랜드
                  </p>
                  <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    re:day
                  </p>
                </div>
                <div className="bg-white p-10 rounded-2xl shadow-sm border border-stone-100">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-shield-check-line text-3xl text-amber-700"></i>
                  </div>
                  <h4 className="text-2xl font-semibold text-stone-800 mb-4" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                    기능성 워크웨어
                  </h4>
                  <p className="text-base text-stone-600 leading-relaxed">
                    초경량 단열 소재인 에어로젤 코팅 섬유를 활용해<br />
                    극한 환경에서도 사람을 보호하는<br />
                    기능성 워크웨어를 만들고 있습니다.
                  </p>
                </div>
              </div>
            </div>

            {/* 클로징 */}
            <div className="max-w-4xl mx-auto text-center">
              <div className="bg-emerald-50 p-10 rounded-2xl mb-12">
                <p className="text-2xl font-semibold text-emerald-800 leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                  기술은 사람을 자립하게 하고<br />
                  자립은 사람을 존중받게 합니다.
                </p>
              </div>

              <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-12 rounded-2xl shadow-xl mb-12">
                <h4 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Daygreen
                </h4>
                <p className="text-xl text-white/95" style={{ fontFamily: "'Noto Sans KR', sans-serif" }}>
                  기술로 자립하고, 존중으로 지속된다.
                </p>
              </div>

              <a
                href="/about"
                className="inline-flex items-center gap-3 px-8 py-4 bg-stone-800 text-white rounded-full hover:bg-stone-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="text-base font-medium">전체 이야기 보기</span>
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
